/**
 * MergedTableToolbar - Toolbar für die zusammengeführte Products+Readiness Tabelle
 * Plan 012/013: Merge Products + Channel Readiness + Configurable Filters
 *
 * Zeigt: Filter-Bar (sales chips) + Issue-Filter-Chip + Sucheingabe + ColumnPicker
 */

import { Search, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Table, VisibilityState } from '@tanstack/react-table'
import { Button } from '@/components/v2/ui'
import { ProductColumnPicker } from './ProductColumnPicker'
import { ProductFilterBar } from './ProductFilterBar'
import type { Product, ProductWithReadiness } from '@/types/products'
import type { IssueFilter } from '@/lib/api/channel-readiness'
import type { ProductTableFilters } from '@/hooks/useProductTableConfig'

interface MergedTableToolbarProps {
  table: Table<ProductWithReadiness>
  search: string
  onSearchChange: (v: string) => void
  onSearchSubmit: () => void
  filters: ProductTableFilters
  onFiltersChange: (f: ProductTableFilters) => void
  onFiltersReset: () => void
  activeIssueFilter?: IssueFilter | null
  onClearIssueFilter: () => void
  onVisibilityChange: (v: VisibilityState) => void
}

export function MergedTableToolbar({
  table, search, onSearchChange, onSearchSubmit,
  filters, onFiltersChange, onFiltersReset,
  activeIssueFilter, onClearIssueFilter, onVisibilityChange,
}: MergedTableToolbarProps) {
  const { t } = useTranslation()
  return (
    <div className="merged-table-toolbar space-y-2 py-2">
      <ProductFilterBar filters={filters} onFiltersChange={onFiltersChange} onReset={onFiltersReset} />
      <div className="flex items-center gap-2 flex-wrap">
        {activeIssueFilter && (
          <div className="flex items-center gap-1 bg-primary-100 dark:bg-primary-950 text-primary-700 dark:text-primary-300 border border-primary-300 dark:border-primary-700 rounded-full px-3 py-1 text-xs font-medium">
            <span className="truncate max-w-[200px]" title={activeIssueFilter.message}>
              {activeIssueFilter.message}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearIssueFilter}
              aria-label={t('products.filter_issue_clear')}
              className="h-auto p-0 ml-1 text-primary-500 hover:text-primary-700"
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        )}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
          <input
            type="text"
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && onSearchSubmit()}
            placeholder={t('products.search_placeholder')}
            className="h-10 w-full rounded-md border border-neutral-300 bg-white pl-9 pr-3 text-sm
              placeholder:text-neutral-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500
              dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100"
          />
        </div>
        <ProductColumnPicker
          table={table as unknown as Table<Product>}
          onVisibilityChange={onVisibilityChange}
        />
      </div>
    </div>
  )
}
